import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';

type LimitField = 'annual_limit' | 'limit';

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * The limit value (in cents) from the most specific limits-status scope that
 * explicitly provides one. Scopes are consulted most- to least-specific:
 * scheme → batch → product → company. A payload value of `0` is meaningful
 * ("no limit") and wins over the plan-level fallback, so any finite number is
 * accepted here. Returns `null` when no scope provides a value.
 */
export function limitStatusValueCents(
  limitsStatus: LimitsStatusDataSignature | undefined,
  field: LimitField
): number | null {
  const details = limitsStatus?.details;
  const bySpecificity = [details?.scheme, details?.batch, details?.product, details?.company];
  for (const detail of bySpecificity) {
    const value = detail?.[field];
    if (isFiniteNumber(value)) {
      return value;
    }
  }
  return null;
}

/**
 * Effective limit (in cents): the most specific limits-status scope wins, with
 * the plan-level column as the fallback. Returns `null` when neither provides a
 * value.
 *
 * Use this anywhere a plan's annual/lifetime limit is shown so the SLR
 * dashboard and the benefit-detail drawer report the same (correct) amount —
 * the dashboard previously read the raw plan column and ignored scope overrides.
 */
export function effectiveLimitCents(
  limitsStatus: LimitsStatusDataSignature | undefined,
  field: LimitField,
  planFallbackCents: number | null | undefined
): number | null {
  return limitStatusValueCents(limitsStatus, field) ?? planFallbackCents ?? null;
}
